import React, { useEffect, useState } from 'react';
import { Button, CardBody, Label, Modal, ModalBody, ModalHeader, Row } from "reactstrap";
import '../../../global/GlobalCSS.css';
import { useQuery } from 'react-query';
import { queryKeyes } from 'shared/queryKeys';
import { RootState } from 'index';
import { useSelector } from 'react-redux';
import ErrorComponent from 'Components/ErrorComponent';
import { apiMedia } from 'global/api.global';
import { queryClient } from 'react-query/queryClient';
import { checkInvalidPrimaryKey, dateTimeFormat } from 'GenericForms/Helper';
import { errorToast, successToast } from 'Components/Toasts';
import ReportingFileUpload from 'Components/ReportingFileUpload';
import { GetPageWithID } from '../pendingpage.hook';
import PageNavButton from '../PageNavButton';
import TableLoading from 'Components/TableLoading';
import Message from 'Components/Message';

const CIIPendingFiles = () => {
    /** State variables */
    const [modalState, setModalState] = useState(false);
    const [fileObject, setFileObject] = useState<any>({});
    const { VesselID } = useSelector((state: RootState) => state.Reporting);
    const [CIIFile, setCIIFiles] = useState<any>([{}]);
    const [pageUrl, setPageUrl] = useState("");
    const [emptyFileError, setEmptyFileError] = useState(false);
    const [uploading, setUploading] = useState(false);
    /** State variables end */

    /** Queries */
    /** CII adjustment event's files that are not yet uploaded  */
    useEffect(() => {
        if (!checkInvalidPrimaryKey(VesselID)) {
            return;
        }
        setPageUrl(queryKeyes.pagination.CIIPendingFilesPage.url(VesselID));
    }, [VesselID]);

    const { data: pendingCIIFileUploadingPage, isLoading, isError } = useQuery(
        [queryKeyes.pagination.CIIPendingFilesPage.key, pageUrl, VesselID],
        async () => await GetPageWithID(VesselID, pageUrl),
        {
            enabled: true,
            staleTime: Infinity,
        }
    );
    /** Queries end */

    /** Open close modal */
    function removeBodyCss() {
        document.body.classList.add("no_padding");
    }
    function tog_backdrop() {
        setModalState(!modalState);
        removeBodyCss();
    }

    /**
     * set modal true to upload file
     * set current object to setFileObject
     * @param file 
     * @param obj 
     */
    const handleUploadButtonClick = (obj: any) => {
        setModalState(!modalState);
        setFileObject(obj);
    }

    /** to upload CII Files */
    const PendingFileUploadSubmit = async () => {
        setUploading(true);
        if (Object.keys(CIIFile[0]).length === 0) {
            setEmptyFileError(true);
        } else if (CIIFile && CIIFile.length > 0) {
            setEmptyFileError(false);
            fileObject.vessel_reporting_cii_adjustment_supporting_file_name = CIIFile[0].file
            await apiMedia.put(`/vessel_reporting_cii_adjustment_supporting_file/${fileObject.id}/`, fileObject)
                .then(res => {
                    if (res.status === 200) {
                        setModalState(!modalState);
                        setCIIFiles([{}])
                        successToast("Success! Files uploaded!");
                        queryClient.invalidateQueries(queryKeyes.pagination.CIIPendingFilesPage.key);
                        queryClient.invalidateQueries(queryKeyes.vessel.ViewFilesByReport.key);
                        queryClient.invalidateQueries(queryKeyes.vessel.ViewFilesByVessel.key);
                    }
                }).catch(err => {
                    errorToast(err?.response?.data?.detail);
                })
        }
        setUploading(false);
    }

    return (
        <React.Fragment>
            <Row>
                {isLoading && <TableLoading />}
                {isError && 0 && <ErrorComponent message='Unable to load required data!' />}
            </Row>
            {!isLoading && !isError &&
                <React.Fragment>
                    <CardBody className='p-0 pb-0 mt-2'>
                        <div className="table-responsive table-height-10">
                            <table className="table mb-0">
                                <thead className="table-light">
                                    <tr>
                                        <th className='p-2 align-middle sr-no-width'>#</th>
                                        <th className='p-2 align-middle' style={{ width: '22%' }}>Duration</th>
                                        <th className='p-2 align-middle'>CII adjustment event</th>
                                        <th className='p-2 align-middle'>Document</th>
                                        <th className='p-2 align-middle text-center' style={{ width: '15%' }}>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {pendingCIIFileUploadingPage?.results?.events?.length > 0 ? (
                                        pendingCIIFileUploadingPage.results.events.map((event: any, index: number) => (
                                            <tr key={index}>
                                                <td className="align-middle p-2 text-center">{index + 1}</td>
                                                <td className="align-middle p-2">
                                                    {dateTimeFormat(event?.cii_begin_date_time, false, false, true)} <b>to</b>
                                                    <br />
                                                    {dateTimeFormat(event?.cii_end_date_time, false, false, true)}
                                                </td>
                                                <td className="align-middle p-2">{event?.cii_adjustment_name}</td>
                                                <td className="align-middle p-0" colSpan={2}>
                                                    <table className="table-bordered width-100">
                                                        <tbody>
                                                            {event?.files?.map((field: any, i: number) => (
                                                                <tr key={i}>
                                                                    <td className="align-middle p-2" style={{ width: "70%" }}>
                                                                        {field.cii_document_name}
                                                                    </td>
                                                                    <td className="align-middle p-2 text-center">
                                                                        <Button
                                                                            type="button"
                                                                            className="btn-sm"
                                                                            color="primary"
                                                                            onClick={() => handleUploadButtonClick(field)}
                                                                        >
                                                                            Upload
                                                                        </Button>
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan={5} className="align-middle text-center">
                                                <Message message={'No files are pending for upload.'} type="info" width="100%" />
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                                <tfoot>
                                    <tr>
                                        <td colSpan={5} className="p-2 ">
                                            <PageNavButton
                                                setPageUrl={setPageUrl}
                                                pageobj={pendingCIIFileUploadingPage}
                                                pageUrl={pageUrl}
                                                isLoading={isLoading}
                                            />
                                        </td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    </CardBody>
                    <Modal
                        size='md'
                        isOpen={modalState}
                        toggle={() => tog_backdrop()}
                        backdrop={"static"}
                        id="staticBackdrop"
                    >
                        <ModalHeader className='p-2'>Upload Pending CII Adjustment File
                            <button
                                onClick={async () => {
                                    setModalState(false);
                                    setCIIFiles([{}])
                                    setEmptyFileError(false);
                                }}
                                type="button"
                                className="close"
                                data-dismiss="modal"
                                aria-label="Close"
                            >
                                <span aria-hidden="true">&times;</span>
                            </button>
                        </ModalHeader>
                        <ModalBody className='p-2'>
                            <ReportingFileUpload
                                setFile={setCIIFiles}
                                file={CIIFile}
                                index={0}
                                sm={5}
                            />
                            {(emptyFileError && Object.keys(CIIFile[0]).length === 0) &&
                                <Label className="error margin-ve-0-7">Please select a file to upload.</Label>
                            }
                            <div className="mt-1 mb-5">
                                <Button
                                    type="submit"
                                    color='primary'
                                    className='pos-end'
                                    disabled={uploading || !CIIFile[0]?.file}
                                    onClick={() => {
                                        PendingFileUploadSubmit()
                                    }}
                                >  {uploading && <i className="spinner-border spinner-border-sm text-light me-2" />}
                                    Save
                                </Button>
                            </div>
                        </ModalBody>
                    </Modal>
                </React.Fragment>
            }
        </React.Fragment>
    )
}
export default CIIPendingFiles